/**
 * Choose the design techniques for one planned graphic. Fit is a judgement about content, so a
 * model makes it.
 *
 * Earlier scenes are passed with their copy so repetition can be judged rather than merely
 * avoided — successive list items used as back-to-back titles should look alike, the same technique
 * on unrelated scenes should not.
 *
 * The call runs on the conversation's own model. A second model would put another provider, another
 * rate-limit bucket and another price on the critical path of every scene, to rank one-line
 * summaries against a short brief.
 */
import { LLMClient } from 'js/src/agent/LLMClient';
import { MessageRole } from 'js/src/agent/types';

import type { TechniqueCandidate } from './suggestTechniques';
import type { ConversationMessage } from 'js/src/agent/types';
import type { ScenePlan } from 'js/src/data/html-design';
import { ErrorReporter } from 'js/src/libs/error-reporter';

const CHOOSER_TASK = 'html_design_technique_choice';

export interface EarlierScene {
    text: string[];
    skills: string[];
}

export interface ChosenTechnique {
    skill: string;
    /** Becomes the assignment's `for`, so it is read by whoever authors the scene. */
    why: string;
}

const SYSTEM_PROMPT = `You choose the motion-design techniques for one scene of a motion graphics video.

You are given the scene's copy and layout, the library of techniques available, and every earlier
scene in this video with the techniques it used. Pick ONE technique, or TWO when the frame genuinely
needs a second doing a different job — one carrying the copy and one serving what the frame is made
of. Never more than two.

How to choose:

1. FIT FIRST. The technique has to make sense of what this scene actually says. A strike-through
   belongs on a correction or a replacement, not on two cities being compared. A typewriter belongs
   on something being composed or queried. A count-up belongs on a quantity, not on a year. If a
   technique would be arbitrary against this copy, it is the wrong pick however interesting it is.

2. PREFER THE MORE INTERESTING OPTION. Where two techniques both fit, take the one with more in it.
   Each candidate is marked "adventurous": true or false — false means a plain entrance, type that
   rises or fades or slides. Those are not wrong, but they are what gets chosen by default and a
   video made entirely of them looks unmade. Choose a plain one when the frame genuinely wants
   restraint — a sombre line, a legal note, a quiet close — and say so in your reason.

3. VARIETY ACROSS THE VIDEO, BUT REPETITION WHERE IT IS EARNED. A candidate marked
   "usedAlready": true was assigned to an earlier scene. Picking one again is correct when this
   scene is the same *kind* of moment as that one — successive items of one list used as
   back-to-back scene titles, the two halves of a comparison, a recurring section header. Those
   should look like each other. Otherwise take something the video has not used: repeating a
   technique across unrelated scenes is a tic, and the viewer reads it as one effect applied to a
   video rather than as each moment being given what it needs.

Answer with JSON only, no prose around it:
{"picks": [{"skill": "<exact name from the library>", "why": "<one clause: what it does for this frame>"}]}

Every "skill" must be copied exactly from the library below. Do not invent names. The "why" is shown
to whoever builds the scene, so make it about this frame rather than about the technique.`;

function userPrompt(
    frame: ScenePlan,
    candidates: TechniqueCandidate[],
    earlier: EarlierScene[],
): string {
    const lines: string[] = ['## This scene', ''];
    lines.push(`Copy on screen: ${frame.text.length ? JSON.stringify(frame.text) : 'none'}`);
    lines.push(
        `Graphics planned: ${frame.graphics.length ? JSON.stringify(frame.graphics) : 'none'}`,
    );
    lines.push(`Layout: ${frame.layout || 'unspecified'}`);
    // Whether the scene plots anything, and how many figures, is the strongest signal between a
    // chart technique, a single counter and a proportion drawn as a metaphor.
    const series = (frame.data ?? []).map(
        ({ label, values }) => `${label} (${values.length}: ${values.join(', ')})`,
    );
    lines.push(`Figures plotted: ${series.length ? series.join('; ') : 'none'}`);
    lines.push('');

    if (earlier.length) {
        lines.push('## Earlier scenes in this video', '');
        earlier.forEach((scene, index) => {
            lines.push(
                `${index + 1}. copy ${JSON.stringify(scene.text)} — used ${
                    scene.skills.length ? scene.skills.join(', ') : 'nothing'
                }`,
            );
        });
        lines.push('');
    } else {
        lines.push('## Earlier scenes in this video', '', 'None — this is the first.', '');
    }

    lines.push('## The library', '');
    for (const { skill, summary, adventurous, usedAlready } of candidates) {
        lines.push(
            `- ${skill} (adventurous: ${adventurous}, usedAlready: ${usedAlready}) — ${summary}`,
        );
    }
    lines.push('', 'Return one or two picks.');
    return lines.join('\n');
}

/** Nothing survives that was not on offer, so the caller can trust every name it gets back. */
function validate(raw: unknown, candidates: TechniqueCandidate[]): ChosenTechnique[] {
    const picks = (raw as { picks?: unknown })?.picks;
    if (!Array.isArray(picks)) {
        return [];
    }
    const available = new Set(candidates.map(({ skill }) => skill));
    const chosen: ChosenTechnique[] = [];
    for (const pick of picks) {
        const skill = (pick as { skill?: unknown })?.skill;
        const why = (pick as { why?: unknown })?.why;
        if (typeof skill !== 'string' || !available.has(skill)) {
            continue;
        }
        if (chosen.some((already) => already.skill === skill)) {
            continue;
        }
        chosen.push({
            skill,
            why: typeof why === 'string' && why.trim() ? why.trim() : 'chosen for this frame',
        });
    }
    return chosen;
}

function parseJson(content: string): unknown {
    const match = content.match(/\{[\s\S]*\}/);
    if (!match) {
        return null;
    }
    try {
        return JSON.parse(match[0]);
    } catch {
        return null;
    }
}

/** Never throws: an empty result means the caller should fall back. */
export async function chooseTechniques(
    frame: ScenePlan,
    candidates: TechniqueCandidate[],
    earlier: EarlierScene[],
    model: string,
    abortSignal?: AbortSignal,
): Promise<ChosenTechnique[]> {
    if (candidates.length === 0) {
        return [];
    }
    try {
        const client = new LLMClient(null, model);
        const response = await client.complete(
            [
                { role: MessageRole.SYSTEM, content: SYSTEM_PROMPT },
                { role: MessageRole.USER, content: userPrompt(frame, candidates, earlier) },
            ] as ConversationMessage[],
            undefined,
            abortSignal,
            CHOOSER_TASK,
            { responseFormat: { type: 'json_object' } },
        );
        return validate(parseJson(response.content), candidates);
    } catch (error) {
        // A cancelled build and a rate limit are both expected; nothing here stalls a build.
        // Cancellation arrives as an AbortError from the request itself, and as a plain Error from
        // a retry backoff.
        const message = error instanceof Error ? error.message : String(error);
        const expected =
            (error as { name?: string })?.name === 'AbortError' ||
            /Rate limit exceeded|Daily usage limit reached|Request aborted/.test(message);
        if (!expected) {
            ErrorReporter.capture(error instanceof Error ? error : new Error(message), {
                extra: { context: CHOOSER_TASK },
            });
        }
        return [];
    }
}
